import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useActiveJobs } from "@/hooks/use-active-jobs";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

/** Done-toast title per job type. Mirrors the running labels in the sidebar. */
const JOB_DONE_LABEL: Record<string, string> = {
  "ad-graphic": "Grafika gotowa",
  graphic: "Grafika gotowa",
  video: "Wideo gotowe",
};

/**
 * JobCompletionNotifier — headless (renders nothing). VP-113.
 *
 * Watches the user's active background generations (same 4s poll as the badge)
 * and, when one LEAVES the active list (i.e. finished), confirms its final status
 * and toasts "ready" with a click-to-open deep link — so a finished generation
 * surfaces no matter which section the user navigated to.
 *
 * Only successful results are announced here. Errors stay handled in the
 * generating view (which toasts + resets), so we never double-toast.
 *
 * Mount once, high in the tree (next to the VP-logo badge).
 */
export function JobCompletionNotifier(): null {
  const { jobs } = useActiveJobs();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  // Job ids seen active on the previous poll (+ what we need to build the toast).
  // `null` until the first poll so we never toast for jobs already running/finished
  // when this mounted.
  const seenRef = useRef<Map<string, { type: string; deepLink: string }> | null>(null);

  useEffect(() => {
    const current = new Map(jobs.map((j) => [j.id, { type: j.type, deepLink: j.deepLink }]));
    const prev = seenRef.current;
    seenRef.current = current;
    if (prev === null) return; // first poll — seed only, don't announce

    for (const [id, info] of prev) {
      if (current.has(id)) continue; // still running
      // Dropped from the active list → finished. Confirm done (not error/expired).
      void apiRequest("GET", `/api/jobs/${id}`)
        .then((job: { status?: string; deepLink?: string }) => {
          if (job?.status !== "done") return;
          const target = job.deepLink ?? info.deepLink;
          toast({
            title: JOB_DONE_LABEL[info.type] ?? "Gotowe",
            description: "Kliknij, by zobaczyć wynik.",
            action: target ? (
              <ToastAction altText="Zobacz wynik" onClick={() => navigate(target)}>
                Zobacz
              </ToastAction>
            ) : undefined,
          });
        })
        .catch(() => {
          /* missing/expired job → no toast (non-fatal) */
        });
    }
  }, [jobs, navigate, toast]);

  return null;
}
